import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import { forwardRef } from 'react';
import { cn } from './cn.js';
const DELTA_ICON = {
    up: ArrowUpRight,
    down: ArrowDownRight,
    flat: Minus,
};
function intentFor(delta) {
    if (delta.intent)
        return delta.intent;
    if (delta.direction === 'up')
        return 'positive';
    if (delta.direction === 'down')
        return 'negative';
    return 'neutral';
}
// Non-hero chip colours by intent; on the orange hero everything is a translucent
// wash of the on-primary ink so it stays readable without a second hue.
const CHIP_INTENT = {
    positive: 'bg-success/12 text-success',
    negative: 'bg-destructive/12 text-destructive',
    neutral: 'bg-muted text-foreground/80',
};
/**
 * KPI tile: a mono headline number with an optional delta chip. Set `hero` on
 * exactly one tile per row for the filled-orange lead treatment.
 */
export const StatCard = forwardRef(({ className, label, value, delta, icon, hero = false, footnote, chart, ...props }, ref) => {
    const DeltaIcon = delta ? DELTA_ICON[delta.direction] : null;
    const chipClass = hero
        ? 'bg-primary-foreground/15 text-primary-foreground'
        : delta
            ? CHIP_INTENT[intentFor(delta)]
            : '';
    return (_jsxs("div", { ref: ref, className: cn('flex flex-col gap-3 rounded-xl border p-5', hero
            ? 'border-transparent bg-primary text-primary-foreground shadow-elev-2'
            : 'border-border bg-card text-card-foreground shadow-elev-1', className), ...props, children: [_jsxs("div", { className: "flex items-center justify-between gap-2", children: [_jsx("span", { className: cn('text-sm font-medium', hero ? 'text-primary-foreground/80' : 'text-muted-foreground'), children: label }), icon && (_jsx("span", { className: hero ? 'text-primary-foreground/80' : 'text-muted-foreground', "aria-hidden": "true", children: icon }))] }), _jsxs("div", { className: "flex flex-wrap items-end justify-between gap-x-3 gap-y-1.5", children: [_jsx("span", { className: "font-display text-3xl font-semibold leading-none tracking-tight tabular-nums", children: value }), delta && DeltaIcon && (_jsxs("span", { className: cn('inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap rounded-full px-2 py-0.5 text-2xs font-semibold tabular-nums', chipClass), children: [_jsx(DeltaIcon, { className: "size-3", "aria-hidden": "true" }), delta.value] }))] }), chart && _jsx("div", { className: "mt-1", children: chart }), footnote && (_jsx("span", { className: cn('text-xs', hero ? 'text-primary-foreground/70' : 'text-muted-foreground'), children: footnote }))] }));
});
StatCard.displayName = 'StatCard';
